import React from 'react';
import {
  Box,
  CardActionArea,
  Divider,
  FormHelperText,
  IconButton,
  InputLabel,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {ManageClientService} from '@store/clients/services';
import {ManageSchedule, NumeralDayOfMonth, ScheduleFrequency, WeekDay} from '@store/common/schedule.model';

import {AddWhite, CheckboxChecked, CheckboxUnchecked, Delete} from '@icons/index';

import dayjs, {globalWeekDaysShort} from '@utils/dayjs.utils';

import {Sizes} from '@constants/sizes.constants';
import {Colors} from '@constants/colors.constants';
import SelectComponent from '@components/inputs/select/Select.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import TimeTextFieldComponent from '@components/inputs/timePicker/TimeTextFieldComponent';

interface OperationalRulesFormProps {
  errors: { [key: string]: any };
  isCreation?: boolean;
  service: ManageClientService;
  updateService: (data: ManageClientService) => void;
}

const OperationalRulesForm = (props: OperationalRulesFormProps) => {
  const {errors, isCreation, service, updateService} = props;

  const {t} = useTranslation();

  const updateSchedule = (index: number, data: ManageSchedule, frequencyUpdate?: boolean) => {
    const schedules = [...(service.schedules || [])];
    if (frequencyUpdate) {
      schedules[index] = {
        name: schedules[index].name,
        theoreticalQuantity: schedules[index].theoreticalQuantity,
        fromDate: schedules[index].fromDate,
        toDate: schedules[index].toDate,
        startHour: schedules[index].startHour,
        endHour: schedules[index].endHour,
        startMinutes: schedules[index].startMinutes,
        endMinutes: schedules[index].endMinutes,
        startFrom: schedules[index].startFrom,
        endFrom: schedules[index].endFrom,
        numberOfOccurrencesAhead: schedules[index].numberOfOccurrencesAhead,
        ...data,
      };
    } else {
      schedules[index] = {...schedules[index], ...data};
    }
    updateService({schedules});
  };

  return <>
    <Stack py="20px" spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography fontSize={16} fontWeight="700">
          {t('clients.details.contractedServices.manage.operationalRules')}
        </Typography>
        <IconButton
          aria-label="small-square-text"
          centerRipple={false}
          onClick={() =>
            updateService({
              schedules: (service.schedules || []).concat({
                dailyEveryWeekDay: false,
                isDayOfMonth: true,
                startFrom: dayjs().utc().set('hour', 7).set('minutes', 0).toDate(),
                endFrom: dayjs().utc().set('hour', 15).set('minutes', 0).toDate(),
              }),
            })
          }
        >
          <AddWhite/>
        </IconButton>
      </Stack>
      <Stack spacing={3}>
        {service.schedules?.map((schedule, index) => (
          <Stack key={`schedule ${index}`} spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" mr="-7px">
              <Box flex={1}>
                <NumberTextFieldComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('clients.details.contractedServices.manage.theoreticalQuantity')}
                  placeholder={t('global.writeSomething')}
                  pattern={new RegExp(/^(\d*|\d+)$/)}
                  handleChange={(theoreticalQuantity) =>
                    updateSchedule(index, {
                      theoreticalQuantity,
                      wasUpdated: true,
                    })
                  }
                  required
                  value={schedule.theoreticalQuantity ?? undefined}
                  error={errors.schedules[index].theoreticalQuantity}
                />
              </Box>
              <IconButton
                onClick={() =>
                  updateService({
                    schedulesToDelete: schedule.id
                      ? [...(service.schedulesToDelete || []), schedule.id]
                      : service.schedulesToDelete,
                    schedules: (service.schedules || []).filter((s, sIndex) => sIndex !== index),
                  })
                }
              >
                <Delete/>
              </IconButton>
            </Stack>
            <NumberTextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('clients.details.contractedServices.manage.numberOfOccurrencesAhead')}
              placeholder={t('global.writeSomething')}
              pattern={new RegExp(/^(\d*|\d+)$/)}
              disabled={!isCreation && !!schedule.id}
              handleChange={(numberOfOccurrencesAhead) =>
                updateSchedule(index, {
                  numberOfOccurrencesAhead,
                  wasUpdated: true,
                })
              }
              required
              value={schedule.numberOfOccurrencesAhead ?? undefined}
              error={errors.schedules[index].numberOfOccurrencesAhead}
              />
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
                {t('operations.executions.manage.schedule')}*
              </InputLabel>
              <Box flex={0.5}>
                <TimeTextFieldComponent
                  label={t('global.scheduleFrom')}
                  handleChange={(startFrom) => {
                    if (startFrom) {
                      updateSchedule(index, { wasUpdated: true, startFrom: startFrom, startHour: dayjs(startFrom).utc().hour(), startMinutes: dayjs(startFrom).utc().minute()}, false);
                    }
                  }}
                  error={errors.startFrom}
                  value={schedule.startFrom}
                />
              </Box>
              <Box flex={0.5}>
                <TimeTextFieldComponent
                  label={t('global.scheduleTo')}
                  handleChange={(endFrom) => {
                    if (endFrom) {
                      updateSchedule(index, {wasUpdated: true, endFrom: endFrom, endHour: dayjs(endFrom).utc().hour(), endMinutes: dayjs(endFrom).utc().minute()}, false);
                    }
                  }}
                  error={errors.endFrom}
                  value={schedule.endFrom}
                />
              </Box>
            </Stack>
            <SelectComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('clients.details.contractedServices.manage.frequency')}
              disabled={!isCreation && !!schedule.id}
              placeholder={t('global.selectOption')}
              handleChange={(frequency) => updateSchedule(index, {frequency}, true)}
              value={schedule.frequency || ''}
              readOnly={!isCreation && !!schedule.id}
              items={ScheduleFrequency.selectItems()}
              error={errors.schedules[index].frequency}
              required
            />
            {schedule.frequency === ScheduleFrequency.DAILY && (
              <Stack pl="145px" spacing={2}>
                <Stack>
                  <Stack direction="row" alignItems="center">
                    <Radio
                      checkedIcon={<CheckboxChecked/>}
                      icon={<CheckboxUnchecked/>}
                      disabled={!isCreation && !!schedule.id}
                      checked={!schedule.dailyEveryWeekDay || false}
                      onChange={() => updateSchedule(index, {dailyEveryWeekDay: false})}
                    />
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      color={errors.schedules[index].dailyFrequencyNumber ? Colors.error : Colors.textWithOpacity}
                    >
                      <Typography fontSize={14} fontWeight="400">
                        Tous les
                      </Typography>
                      <NumberTextFieldComponent
                        inputWidth={80}
                        pattern={new RegExp(/^(\d*|\d+)$/)}
                        handleChange={(dailyFrequencyNumber) => updateSchedule(index, {dailyFrequencyNumber})}
                        value={schedule.dailyFrequencyNumber ?? undefined}
                        readOnly={!isCreation && !!schedule.id}
                        disabled={schedule.dailyEveryWeekDay || !isCreation && !!schedule.id}
                      />
                      <Typography fontSize={14} fontWeight="400">
                        jour(s)
                      </Typography>
                    </Stack>
                  </Stack>
                  {errors.schedules[index].dailyFrequencyNumber && (
                    <Box pl="38px">
                      <FormHelperText error>{errors.schedules[index].dailyFrequencyNumber}</FormHelperText>
                    </Box>
                  )}
                </Stack>
                <Stack direction="row" alignItems="center">
                  <Radio
                    checkedIcon={<CheckboxChecked/>}
                    icon={<CheckboxUnchecked/>}
                    checked={schedule.dailyEveryWeekDay || false}
                    disabled={!isCreation && !!schedule.id}
                    onChange={() => updateSchedule(index, {dailyEveryWeekDay: true})}
                  />
                  <Stack direction="row" alignItems="center" spacing={1} color={Colors.textWithOpacity}>
                    <Typography fontSize={14} fontWeight="400">
                      Tous les jours ouvrables
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            )}
            {schedule.frequency === ScheduleFrequency.WEEKLY && (
              <Stack direction="row" spacing={2}>
                <Box width={Sizes.drawerInputLabelWidth}/>
                <Stack spacing={2}>
                  <Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        fontSize={14}
                        fontWeight="400"
                        color={errors.schedules[index].weeklyFrequencyNumber ? Colors.error : Colors.textWithOpacity}
                      >
                        Toutes les
                      </Typography>
                      <NumberTextFieldComponent
                        inputWidth={80}
                        pattern={new RegExp(/^(\d*|\d+)$/)}
                        readOnly={!isCreation && !!schedule.id}
                        handleChange={(weeklyFrequencyNumber) => updateSchedule(index, {weeklyFrequencyNumber})}
                        value={schedule.weeklyFrequencyNumber ?? undefined}
                      />
                      <Typography
                        fontSize={14}
                        fontWeight="400"
                        color={errors.schedules[index].weeklyFrequencyNumber ? Colors.error : Colors.textWithOpacity}
                      >
                        semaine(s) le
                      </Typography>
                    </Stack>
                    {errors.schedules[index].weeklyFrequencyNumber && (
                      <FormHelperText error>{errors.schedules[index].weeklyFrequencyNumber}</FormHelperText>
                    )}
                  </Stack>
                  <Stack>
                    <Stack direction="row" spacing={1}>
                      {globalWeekDaysShort.map((day, dIndex) => (
                        <CardActionArea
                          key={`day ${dIndex}`}
                          aria-label="week-day"
                          aria-selected={!!schedule.daysOfWeek?.includes(dIndex + 1)}
                          disabled={!isCreation && !!schedule.id}
                          onClick={() => {
                            const indexOfDay = schedule.daysOfWeek?.indexOf(dIndex + 1) ?? -1;

                            if (indexOfDay !== -1) {
                              updateSchedule(index, {
                                daysOfWeek: (schedule.daysOfWeek || []).filter((d) => d !== dIndex + 1),
                              });
                            } else {
                              updateSchedule(index, {daysOfWeek: (schedule.daysOfWeek || []).concat(dIndex + 1)});
                            }
                          }}
                        >
                          {day}
                        </CardActionArea>
                      ))}
                    </Stack>
                    {errors.schedules[index].daysOfWeek && (
                      <FormHelperText error>{errors.schedules[index].daysOfWeek}</FormHelperText>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            )}
            {schedule.frequency === ScheduleFrequency.MONTHLY && (
              <Stack spacing={2}>
                <Stack pl="145px" spacing={2}>
                  <Stack>
                    <Stack direction="row" alignItems="center">
                      <Radio
                        checkedIcon={<CheckboxChecked/>}
                        icon={<CheckboxUnchecked/>}
                        disabled={!isCreation && !!schedule.id}
                        checked={schedule.isDayOfMonth || false}
                        onChange={() => updateSchedule(index, {isDayOfMonth: true})}
                      />
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        color={errors.schedules[index].dayOfMonth ? Colors.error : Colors.textWithOpacity}
                      >
                        <Typography fontSize={14} fontWeight="400">
                          Le
                        </Typography>
                        <NumberTextFieldComponent
                          inputWidth={80}
                          pattern={new RegExp(/^(\d*|\d+)$/)}
                          handleChange={(dayOfMonth) => updateSchedule(index, {dayOfMonth})}
                          value={schedule.dayOfMonth ?? undefined}
                          readOnly={!isCreation && !!schedule.id}
                          disabled={!schedule.isDayOfMonth}
                        />
                      </Stack>
                    </Stack>
                    {errors.schedules[index].dayOfMonth && (
                      <Box pl="38px">
                        <FormHelperText error>{errors.schedules[index].dayOfMonth}</FormHelperText>
                      </Box>
                    )}
                  </Stack>
                  <Stack>
                    <Stack direction="row" alignItems="center">
                      <Radio
                        checkedIcon={<CheckboxChecked/>}
                        icon={<CheckboxUnchecked/>}
                        disabled={!isCreation && !!schedule.id}
                        checked={!schedule.isDayOfMonth || false}
                        onChange={() => updateSchedule(index, {isDayOfMonth: false})}
                      />
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        color={
                          errors.schedules[index].numeralDayOfMonth || errors.schedules[index].weekDayForMonth
                            ? Colors.error
                            : Colors.textWithOpacity
                        }
                      >
                        <Typography fontSize={14} fontWeight="400">
                          Le
                        </Typography>
                        <SelectComponent
                          inputWidth={140}
                          handleChange={(numeralDayOfMonth) => updateSchedule(index, {numeralDayOfMonth})}
                          value={schedule.numeralDayOfMonth}
                          items={NumeralDayOfMonth.selectItems()}
                          readOnly={!isCreation && !!schedule.id}
                          disabled={schedule.isDayOfMonth}
                        />
                        <SelectComponent
                          inputWidth={130}
                          handleChange={(weekDayForMonth) => updateSchedule(index, {weekDayForMonth})}
                          value={schedule.weekDayForMonth}
                          items={WeekDay.selectItems}
                          readOnly={!isCreation && !!schedule.id}
                          disabled={schedule.isDayOfMonth}
                        />
                      </Stack>
                    </Stack>
                    {errors.schedules[index].numeralDayOfMonth && errors.schedules[index].weekDayForMonth && (
                      <Box pl="38px">
                        <FormHelperText error>
                          {errors.schedules[index].numeralDayOfMonth || errors.schedules[index].weekDayForMonth}
                        </FormHelperText>
                      </Box>
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Box width={Sizes.drawerInputLabelWidth}/>
                  <Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        fontSize={14}
                        fontWeight="400"
                        color={errors.schedules[index].monthlyFrequencyNumber ? Colors.error : Colors.textWithOpacity}
                      >
                        Tous les
                      </Typography>
                      <NumberTextFieldComponent
                        inputWidth={80}
                        pattern={new RegExp(/^(\d*|\d+)$/)}
                        handleChange={(monthlyFrequencyNumber) => updateSchedule(index, {monthlyFrequencyNumber})}
                        readOnly={!isCreation && !!schedule.id}
                        value={schedule.monthlyFrequencyNumber ?? undefined}
                      />
                      <Typography
                        fontSize={14}
                        fontWeight="400"
                        color={errors.schedules[index].monthlyFrequencyNumber ? Colors.error : Colors.textWithOpacity}
                      >
                        mois
                      </Typography>
                    </Stack>
                    {errors.schedules[index].monthlyFrequencyNumber && (
                      <FormHelperText error>{errors.schedules[index].monthlyFrequencyNumber}</FormHelperText>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            )}
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <InputLabel sx={{width: 106}}>
                {t('clients.details.contractedServices.manage.operationalRuleValidity')}*
              </InputLabel>
              <Box flex={0.5}>
                <DatePickerComponent
                  label={t('global.dateFrom')}
                  placeholder={t('global.selectDate')}
                  value={schedule.fromDate}
                  handleChange={(fromDate) =>
                    updateSchedule(index, {
                      fromDate: fromDate,
                      wasUpdated: true,
                    })
                  }
                  error={errors.schedules[index].fromDate}
                />
              </Box>
              <Box flex={0.5}>
                <DatePickerComponent
                  label={t('global.dateTo')}
                  placeholder={t('global.selectDate')}
                  value={schedule.toDate}
                  handleChange={(toDate) => {
                    updateSchedule(index, {
                      toDate: toDate,
                      wasUpdated: true,
                    });
                  }}
                  error={errors.schedules[index].toDate}
                />
              </Box>
            </Stack>
            {index < (service.schedules?.length || 0) - 1 && (
              <Divider style={{opacity: 0.2}} variant="middle" orientation="horizontal"/>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
    <Divider variant="fullWidth" orientation="horizontal"/>
  </>;
};

export default OperationalRulesForm;
